import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

// Axios base configuration
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'https://gokturkgpt-api.gokturkgpt.workers.dev';
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Add JWT token to requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token and get user info
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // You could add a token verification endpoint here
      setUser(JSON.parse(localStorage.getItem('user')));
    }
    setLoading(false);
  }, []);

  const login = async (userData) => {
    try {
      // If userData is already provided (from guest login), use it directly
      if (userData && userData.id) {
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        toast.success(`Hoş geldiniz, ${userData.name}!`);
        return userData;
      }
      
      // Otherwise, treat as Google token
      const response = await axios.post('/api/auth/google', {
        token: userData
      });

      const { access_token, user: userInfo } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userInfo));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(userInfo);
      toast.success(`Hoş geldiniz, ${userInfo.name}!`);
      
      return userInfo;
    } catch (error) {
      toast.error('Giriş yapılırken bir hata oluştu');
      throw error;
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password
      });

      const { access_token, user: userInfo } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userInfo));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(userInfo);
      toast.success(`Hoş geldiniz, ${userInfo.name}!`);
      
      return userInfo;
    } catch (error) {
      console.error('Email login error:', error);
      toast.error('E-posta girişi sırasında hata oluştu');
      throw error;
    }
  };

  const loginWithUser = (userData) => {
    try {
      setUser(userData);
      toast.success(`Hoş geldiniz, ${userData.name}!`);
    } catch (error) {
      toast.error('Giriş sırasında hata oluştu');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('Başarıyla çıkış yapıldı');
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const value = {
    user,
    login,
    loginWithEmail,
    loginWithUser,
    logout,
    updateUser,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
